export const deformedCylindricalProjection = `
#if defined ( USE_DEFORMED_CYLINDRICAL_PROJECTION )

  // Check pseudo code for deformed cylindrical for the original code provided by Client

  // Specifies the maximum percentage by which the equidistant part will be squeezed in vertical direction.
  #define M_MAX_VERTICAL_SQUEEZE 0.375
  #define CUT_ANGLE_LOWER 20.0
  #define CUT_ANGLE_UPPER -20.0

  bool isZero(float val) {
    const float epsilon = 0.00001;
    return abs(val) < epsilon;
  }

  float calculateSqueezeFactorEquidistant(float u) {
    float pixelRatio = 1.0 - (u / principlePoint.x);
    return 1.0 - (M_MAX_VERTICAL_SQUEEZE * (pixelRatio * pixelRatio));
  }

  vec2 lightRaySensorToPixelEquidistant(vec4 vertexInCamera, float cutAngle, float cutHeight) {
    // right handed to ISO8855
    float x = -vertexInCamera.z;
    float y = -vertexInCamera.x;
    float z = vertexInCamera.y;

    vec2 principalPointShift = vec2(0.0, cutHeight);
    vec2 principalPointEquidistant = principlePoint + principalPointShift;

    // Calculate azimuth and altitude
    // azimuth is the same for the cylindrical and spherical part
    float azimuth = atan(-y, x);
    float altitude = atan(-z / length(vec2(x, y)));

    float altitudeEquidistant = altitude - asin(tan(radians(cutAngle)) * cos(altitude));
    vec3 lightRayVectorEquidistant = vec3(
        cos(altitudeEquidistant) * cos(azimuth),
        cos(altitudeEquidistant) * sin(azimuth),
        sin(altitudeEquidistant) * -1.0
    );

    // Use standard f theta (equidistant) implementation from here
    float lightRayLength = length(lightRayVectorEquidistant);

    if(isZero(lightRayLength)) {
       return vec2(-2.0, -2.0);
    }

    // convert from Din70k to CV coordinate system
    vec3 lightRayVectorEquidistantCV = vec3(
          -lightRayVectorEquidistant.y,
          -lightRayVectorEquidistant.z,
          lightRayVectorEquidistant.x
    );

    float radiusEquidistant = length(lightRayVectorEquidistantCV);
    float theta = acos(lightRayVectorEquidistantCV.z / radiusEquidistant);
    float radiusCircle = length(vec2(lightRayVectorEquidistantCV.x, lightRayVectorEquidistantCV.y));

    // Special case where radius == 0: The light_ray_vector_equidistant points exactly towards the optical axis (1,0,0)
    float ratio = isZero(radiusCircle) ? 1.0 : (theta / radiusCircle);
    vec2 pixelNormalized = vec2(ratio * lightRayVectorEquidistantCV.x, ratio * lightRayVectorEquidistantCV.y);
    float u = (pixelNormalized.x * focalLength.x) + principalPointEquidistant.x;
    float squeezeFactor = calculateSqueezeFactorEquidistant(u);
    float v = (pixelNormalized.y * focalLength.y * squeezeFactor) + principalPointEquidistant.y;
    return vec2(u, v);
  }

  vec4 getDeformedCylindricalPosition(vec3 h3Position, mat4 h3ViewMatrix) {
    vec4 vertexWorldPosition = modelMatrix * vec4(h3Position, 1.0 );
    vec4 vertexInCamera = h3ViewMatrix * vertexWorldPosition;

    // right handed to ISO8855
    float x = -vertexInCamera.z;
    float y = -vertexInCamera.x;
    float z = vertexInCamera.y;


    vec2 mCutHeights = vec2 (
      tan(radians(CUT_ANGLE_UPPER)) * focalLength.y,
      tan(radians(CUT_ANGLE_LOWER)) * focalLength.y
    );

    float altitudeAngle = degrees(atan(-z, length(vec2(x, y))));

    vec2 pixel;
    if (altitudeAngle > CUT_ANGLE_LOWER) {
      pixel = lightRaySensorToPixelEquidistant(vertexInCamera, CUT_ANGLE_LOWER, mCutHeights.y);
    } else if (altitudeAngle < CUT_ANGLE_UPPER) {
      pixel = lightRaySensorToPixelEquidistant(vertexInCamera, CUT_ANGLE_UPPER, mCutHeights.x);
    } else {
      pixel = lightRayToPixelCylinder(h3Position, h3ViewMatrix);
    }

    // Client considers (0, 0) of the cylindrical image as the bottom left
    // corner, while we consider it the top left corner.
    vec2 finalPoint = (imageSize - pixel) / imageSize * 2.0 - 1.0;

    vec3 cameraToPointDirection = normalize(vertexWorldPosition.xyz - cameraWorldPosition);

    float behindCameraTolerance = -0.1;
    if(dot(cameraWorldDirection, cameraToPointDirection) < behindCameraTolerance){
      return vec4(finalPoint, -2.0, 1.0);
    }

    return vec4(finalPoint, 1.0, 1.0);
  }

#endif
`;
